import React, { FC } from 'react';
import { FormInstance } from 'antd';
import { Button, Col, Divider, Form, Row, Select, Tooltip, Typography } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon, EditIconLight } from 'components/common/icons';
import { useRelation } from './useRelation';
import styles from './styles.module.scss';
import { usePurchaseBrandMapping } from 'hooks/usePurchaseBrandMapping';
import { useParams } from 'react-router-dom';
import { useSupplierItemMapping } from 'hooks/useSupplierItemMapping';
import { useSupplierItemSelectorListQuery } from 'api';
import { ValidationErrors } from 'constants/validations';
import { SUPPLIER_OFFER_PRIORITIES } from 'feature/dealTabs/commercialOffers/addCommercialOffer/relation/RelationForm';

const { Item } = Form;
const { Text } = Typography;

export type RelationDataType = {
  relationBrand: string;
  relationSupplierOffer: string;
  id: string;
};

interface IRelation {
  onRemove: (id: string) => void;
  onEdit: (brand: string, item: string) => void;
  onCancel: () => void;
  data: RelationDataType;
  form: FormInstance;
  relationList: Record<string, any>[];
  onSave: (relation: any) => void;
  isEditeMode: boolean;
}

export const RelationCard: FC<IRelation> = React.memo((props) => {
  const { data, onSave, relationList, form, onRemove, onEdit, isEditeMode, onCancel } = props;
  const { relationBrand, relationSupplierOffer } = data;
  const { id } = useParams<{ id: string }>() as { id: string };
  // const { hasAccess } = useAccessControl();
  const { purchaseRequestBrandMapping } = usePurchaseBrandMapping(id);
  const { supplierItemMapping } = useSupplierItemMapping(relationBrand);

  const { data: editSupplierOffers = [] } = useSupplierItemSelectorListQuery(
    { purchaseRequestId: relationBrand },
    { skip: !relationBrand }
  );
  const {
    purchaseRequestBrand,
    onEditRelationSave,
    onRelationEdit,
    onRelationRemove,
    onRedirectToSupplierOffer,
    onRedirectToPurchaseRequestSupplierOffers,
  } = useRelation({
    form,
    relationList,
    onSave,
    onRemove,
    onEdit,
  });
  return (
    <Col span={24}>
      {!isEditeMode && (
        <Row className={styles.main} align={'middle'} justify={'space-between'}>
          <Col>
            <Row gutter={[4, 0]} align={'middle'}>
              <Col>
                <Text type={'secondary'} className={styles.relation_type_label}>
                  {purchaseRequestBrandMapping[relationBrand]?.label}
                </Text>
              </Col>
              <Col>
                <Button
                  type={'link'}
                  variant={'link'}
                  style={{
                    color: '#222530',
                    textDecoration: 'underline',
                    padding: 0,
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  onClick={() => onRedirectToSupplierOffer(relationBrand, relationSupplierOffer)}
                >
                  {supplierItemMapping[relationSupplierOffer]?.label}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row align={'middle'}>
              <Col>
                <Button
                  size={'small'}
                  type={'link'}
                  icon={<EditIconLight />}
                  onClick={() => onRelationEdit(relationBrand, relationSupplierOffer)}
                />
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => onRelationRemove(relationBrand, relationSupplierOffer)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isEditeMode && (
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item name="editRelationBrand" label={'Brand/ Purchase Request'}>
              <Select
                onChange={() => {
                  form.resetFields(['editRelationSupplierOffer']);
                }}
                options={purchaseRequestBrand}
                size={'large'}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item noStyle shouldUpdate={(prev, curr) => prev.editRelationBrand !== curr.editRelationBrand}>
              {({ getFieldValue }) => {
                const editRelationBrand = getFieldValue('editRelationBrand');
                return (
                  <Item
                    name="editRelationSupplierOffer"
                    label={'Supplier Offer*'}
                    rules={[{ required: !!editRelationBrand, message: ValidationErrors.required('Supplier Offer') }]}
                  >
                    <Select
                      disabled={!editRelationBrand}
                      size={'large'}
                      options={editSupplierOffers.map((supplier: any) => ({
                        value: supplier.value,
                        disabled: relationList.some(
                          (_relation) =>
                            _relation.relationBrand === editRelationBrand &&
                            _relation.relationSupplierOffer === supplier.value &&
                            !(
                              _relation.relationBrand === relationBrand &&
                              _relation.relationSupplierOffer === relationSupplierOffer
                            )
                        ),
                        label: (
                          <Row justify={'space-between'} align={'middle'}>
                            {supplier.label}
                            {!!supplier.priorities?.length && (
                              <Col>
                                <Row align={'middle'} className={styles.sort_item}>
                                  {supplier.priorities.length > 1 ? (
                                    supplier.priorities.map((_item: string) => (
                                      <Tooltip key={_item} title={SUPPLIER_OFFER_PRIORITIES[_item].label}>
                                        <Text>{SUPPLIER_OFFER_PRIORITIES[_item].icon}</Text>
                                      </Tooltip>
                                    ))
                                  ) : (
                                    <>
                                      {SUPPLIER_OFFER_PRIORITIES[supplier.priorities[0] as string].icon}
                                      <Text className={'font-size-12 font-weight-500'}>
                                        {SUPPLIER_OFFER_PRIORITIES[supplier.priorities[0] as string].label}
                                      </Text>
                                    </>
                                  )}
                                </Row>
                              </Col>
                            )}
                          </Row>
                        ),
                      }))}
                      dropdownRender={() => (
                        <>
                          <Divider />
                          <Row style={{ padding: '8px' }} justify={'center'}>
                            <Button
                              type="text"
                              style={{ color: '#05796B', fontWeight: 500 }}
                              onClick={() => onRedirectToPurchaseRequestSupplierOffers(relationBrand)}
                            >
                              View Details
                            </Button>
                          </Row>
                        </>
                      )}
                    />
                  </Item>
                );
              }}
            </Item>
          </Col>
          <Col span={24}>
            <Row gutter={4} justify={'end'}>
              <Col>
                <Button color="default" variant="filled" onClick={onCancel} icon={<CloseIcon />} />
              </Col>
              <Col>
                <Button
                  onClick={() => onEditRelationSave(relationBrand, relationSupplierOffer)}
                  type={'primary'}
                  icon={<DoneIcon />}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Col>
  );
});
